// NodeRegistry.jsx - Lazy-loaded node component registry with code splitting

import React, { Suspense } from 'react';

/**
 * Lazy-loaded node components for code splitting optimization
 * Each node type is loaded only when needed, reducing initial bundle size
 * Organized by category for better developer experience
 */
const LazyNodeComponents = {
  // Core node types (nodes/core/)
  customInput: React.lazy(() => import('../nodes/core/inputNode').then(module => ({ default: module.InputNode }))),
  llm: React.lazy(() => import('../nodes/core/llmNode').then(module => ({ default: module.LLMNode }))),
  customOutput: React.lazy(() => import('../nodes/core/outputNode').then(module => ({ default: module.OutputNode }))),
  text: React.lazy(() => import('../nodes/core/textNode').then(module => ({ default: module.TextNode }))),

  // Processing node types (nodes/processing/)
  filter: React.lazy(() => import('../nodes/processing/filterNode').then(module => ({ default: module.FilterNode }))),
  transform: React.lazy(() => import('../nodes/processing/transformNode').then(module => ({ default: module.TransformNode }))),
  aggregate: React.lazy(() => import('../nodes/processing/aggregateNode').then(module => ({ default: module.AggregateNode }))),
  conditional: React.lazy(() => import('../nodes/processing/conditionalNode').then(module => ({ default: module.ConditionalNode }))),
  validator: React.lazy(() => import('../nodes/processing/validatorNode').then(module => ({ default: module.ValidatorNode }))),

  // Advanced node types (nodes/advance/)
  api: React.lazy(() => import('../nodes/advance/apiNode').then(module => ({ default: module.APINode }))),
  storage: React.lazy(() => import('../nodes/advance/storageNode').then(module => ({ default: module.StorageNode }))),
  join: React.lazy(() => import('../nodes/advance/joinNode').then(module => ({ default: module.JoinNode }))),
  split: React.lazy(() => import('../nodes/advance/splitNode').then(module => ({ default: module.SplitNode }))),
  delay: React.lazy(() => import('../nodes/advance/delayNode').then(module => ({ default: module.DelayNode }))),
};

/**
 * Loading skeleton component for node components
 * Displays while node components are being loaded
 */
const NodeLoadingSkeleton = ({ nodeType }) => (
  <div className="w-52 min-h-28 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 animate-pulse">
    <div className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-4 h-4 bg-gray-300 rounded"></div>
        <div className="h-4 bg-gray-300 rounded flex-1"></div>
      </div>
      <div className="space-y-2">
        <div className="h-3 bg-gray-300 rounded w-3/4"></div>
        <div className="h-3 bg-gray-300 rounded w-1/2"></div>
      </div>
    </div>
    <div className="absolute -left-2 top-1/2 transform -translate-y-1/2">
      <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
    </div>
    <div className="absolute -right-2 top-1/2 transform -translate-y-1/2">
      <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
    </div>
  </div>
);

/**
 * Error fallback component for failed node loads
 */
const NodeErrorFallback = ({ nodeType, error }) => (
  <div className="w-52 min-h-28 rounded-lg border-2 border-red-200 bg-red-50 p-4">
    <div className="text-red-700 text-sm font-medium mb-2">
      Failed to load {nodeType} node
    </div>
    <div className="text-red-600 text-xs">
      {error?.message || 'Unknown error occurred'}
    </div>
    <button
      onClick={() => window.location.reload()}
      className="mt-2 text-xs text-red-600 underline hover:text-red-800"
    >
      Reload page
    </button>
  </div>
);

/**
 * Error boundary for node component loading errors
 */
class NodeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Node component failed to load:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <NodeErrorFallback
          nodeType={this.props.nodeType}
          error={this.state.error}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Lazy node wrapper component that handles loading states and errors
 * @param {Object} props - Component props
 * @param {string} props.nodeType - Type of node to render
 * @param {Object} props.nodeProps - Props to pass to the node component
 * @returns {React.Component} Wrapped lazy-loaded node component
 */
const LazyNodeWrapper = ({ nodeType, ...nodeProps }) => {
  const LazyComponent = LazyNodeComponents[nodeType];

  if (!LazyComponent) {
    return (
      <div className="w-52 min-h-28 rounded-lg border-2 border-orange-200 bg-orange-50 p-4">
        <div className="text-orange-700 text-sm font-medium">
          Unknown node type: {nodeType}
        </div>
        <div className="text-orange-600 text-xs mt-1">
          This node type is not registered in the system.
        </div>
      </div>
    );
  }

  return (
    <NodeErrorBoundary nodeType={nodeType}>
      <Suspense fallback={<NodeLoadingSkeleton nodeType={nodeType} />}>
        <LazyComponent {...nodeProps} />
      </Suspense>
    </NodeErrorBoundary>
  );
};

/**
 * Create the nodeTypes object for ReactFlow with lazy-loaded components
 * This replaces the direct import approach with code-split lazy loading
 */
export const createLazyNodeTypes = () => {
  const nodeTypes = {};

  Object.keys(LazyNodeComponents).forEach(nodeType => {
    nodeTypes[nodeType] = (props) => (
      <LazyNodeWrapper nodeType={nodeType} {...props} />
    );
  });

  return nodeTypes;
};

/**
 * Preload specific node types for better user experience
 * Call this function to preload commonly used node types
 * @param {Array<string>} nodeTypes - Array of node types to preload
 */
export const preloadNodeTypes = (nodeTypes = []) => {
  nodeTypes.forEach(nodeType => {
    const LazyComponent = LazyNodeComponents[nodeType];
    if (LazyComponent) {
      // Trigger the import but don't wait for it
      LazyComponent.preload?.();
    }
  });
};

/**
 * Get available node types for the toolbar
 * @returns {Array<string>} Array of available node type names
 */
export const getAvailableNodeTypes = () => {
  return Object.keys(LazyNodeComponents);
};

export default LazyNodeWrapper;